import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Scheduled.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../../components/Icon";
import ModalMember from "../../../components/ModalMember";

// Services
import { getUsersByPartner, createUser, updateUser, deleteUser } from "../../../services/user.service";
import { showSuccess, showError } from "../../../services/toast.service";
import ModalTeams from "../../../components/ModalTeams";

const Member = ({ partner }) => {
    const [search, setSearch] = useState("");
    const [members, setMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState([]);

    const fetchMembers = async () => {
        if (!partner || !partner.id) return;
        try {
            setLoading(true);
            const membersData = await getUsersByPartner(partner.id);
            setMembers(membersData);
            setFilteredMembers(membersData);
        } catch (error) {
            console.error('Erreur lors de la récupération des membres:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (partner) {
            fetchMembers();
        }
    }, [partner]);

    const handleOpenModal = (member = null) => {
        setEditingMember(member);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingMember(null);
        setIsModalOpen(false);
    };

    const handleSaveMember = async (formData) => {
        if (!partner || !partner.id) {
            showError("Impossible de sauvegarder le membre sans référence au partenaire.");
            return;
        }
        try {
            if (editingMember) {
                const updatedMember = await updateUser(editingMember.id, formData);
                setMembers(members.map(m => m.id === editingMember.id ? updatedMember : m));
                setFilteredMembers(filteredMembers.map(m => m.id === editingMember.id ? updatedMember : m));
                showSuccess('Utilisateur mis à jour avec succès.');
            } else {
                const dataToCreate = { ...formData, partner_id: partner.id };
                const newMember = await createUser(dataToCreate);
                setMembers([...members, newMember.member || newMember]);
                setFilteredMembers([...filteredMembers, newMember.member || newMember]);
                showSuccess('Utilisateur créé avec succès.');
            }
            handleCloseModal();
            fetchMembers(); // Refresh list
        } catch (error) {
            showError('Erreur lors de la sauvegarde de l\'utilisateur.');
            console.error(error);
        }
    };

    const handleSelectMember = (memberId, isSelected) => {
        if (isSelected) {
            setSelectedMembers([...selectedMembers, memberId]);
        } else {
            setSelectedMembers(selectedMembers.filter(id => id !== memberId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allMemberIds = filteredMembers.map(member => member.id);
            setSelectedMembers(allMemberIds);
        } else {
            setSelectedMembers([]);
        }
    };

    const handleClearSelection = () => {
        setSelectedMembers([]);
    };

    const handleDeleteSelected = async (selectedIds) => {
        if (!selectedIds.length) {
            return;
        }
        
        try {
            // Supprimer chaque membre
            for (const memberId of selectedIds) {
                await deleteUser(memberId);
            }
            
            // Mettre à jour l'état local
            const remainingMembers = members.filter(m => !selectedIds.includes(m.id));
            setMembers(remainingMembers);
            setFilteredMembers(remainingMembers.filter(member => {
                if (!search.trim()) return true;
                const searchLower = search.toLowerCase();
                const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
                const email = (member.expand?.user_id?.email || '').toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            }));
            
            return true;
        } catch (error) {
            console.error('Erreur lors de la suppression des utilisateurs:', error);
            throw error;
        }
    };

    const handleDeleteMember = async (memberId) => {
        try {
            await deleteUser(memberId);
            setMembers(members.filter(m => m.id !== memberId));
            setFilteredMembers(filteredMembers.filter(m => m.id !== memberId));
            showSuccess('Utilisateur supprimé avec succès.');
        } catch (error) {
            showError('Erreur lors de la suppression de l\'utilisateur.');
            console.error(error);
        }
    };

    // Fonction pour filtrer les membres en fonction du texte de recherche
    const filterMembers = (searchText) => {
        if (!searchText.trim()) {
            setFilteredMembers(members);
            return;
        }

        const searchLower = searchText.toLowerCase();
        const filtered = members.filter(member => {
            const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
            const email = (member.expand?.user_id?.email || '').toLowerCase();
            const phone = (member.expand?.user_id?.phone || '').toLowerCase();
            const role = (member.expand?.user_id?.role || []).join(' ').toLowerCase();

            return fullName.includes(searchLower) ||
                email.includes(searchLower) ||
                phone.includes(searchLower) ||
                role.includes(searchLower);
        });

        setFilteredMembers(filtered);
    };

    // Effet pour filtrer les membres lorsque la recherche change
    useEffect(() => {
        filterMembers(search);
    }, [search, members]);

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        filterMembers(search);
    };

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Membres du partenaire"
                classTitle={cn("title-blue", styles.title)}
                head={
                    <>
                      <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={() => handleSubmit()}
                        placeholder="Rechercher par nom, prénom, email"
                        type="text"
                        name="search"
                        icon="search"
                      />
                      <div className={styles.sorting}>
                        <button className={cn("button-stroke", styles.button)} onClick={() => handleOpenModal()}>
                            <Icon name="plus" size="16" />
                            <span>Ajouter un membre</span>
                        </button>
                      </div>
                    </>
                  }
                >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className="spinner-large" />
                        </div>
                    ) : filteredMembers.length > 0 ? (
                        <Table 
                            items={filteredMembers} 
                            title="Membres du partenaire" 
                            onEdit={handleOpenModal}
                            onDelete={handleDeleteMember}
                            selectedMembers={selectedMembers}
                            onSelectMember={handleSelectMember}
                            onSelectAll={handleSelectAll}
                        />
                    ) : members.length > 0 ? (
                        <div className={styles.noMembers}>Aucun membre ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucun membre trouvé pour ce partenaire.</div>
                    )}
                </div>
            </Card>

            <ModalTeams
                visible={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveMember}
                member={editingMember}
            />

            <Panel 
                selectedMembers={selectedMembers} 
                onDeleteSelected={handleDeleteSelected} 
                onClearSelection={handleClearSelection} 
            />
        </>
    );
};

export default Member;
